// Markdown Toolbar Web Component
import { insertTextAtCursor } from '../utils/text-insertion.js';

class MarkdownToolbar extends HTMLElement {
  constructor() {
    super();
    this.targetTextarea = null;
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.helpPaneOpen = false;
  }

  static get observedAttributes() {
    return ['target', 'show-sparkle'];
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.findTargetTextarea();
  }

  disconnectedCallback() {
    if (this.targetTextarea) {
      this.targetTextarea.removeEventListener('keydown', this.boundHandleKeydown);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'target' && this.isConnected) {
      this.findTargetTextarea();
    }
  }

  findTargetTextarea() {
    const targetAttr = this.getAttribute('target');

    if (targetAttr) {
      // If target is specified, find it
      const targetElement = document.querySelector(targetAttr) ||
                           document.getElementById(targetAttr);
      if (targetElement && targetElement.tagName === 'TEXTAREA') {
        this.targetTextarea = targetElement;
      }
    } else {
      // If we're inside markdown-preview, find textarea in the preview component
      const previewWrapper = this.closest('markdown-preview');
      if (previewWrapper) {
        const textarea = previewWrapper.querySelector('.markdown-textarea-container textarea');
        if (textarea) {
          this.targetTextarea = textarea;
        }
      } else {
        // Otherwise, find the next textarea sibling or parent's textarea
        let current = this.nextElementSibling;
        while (current) {
          if (current.tagName === 'TEXTAREA') {
            this.targetTextarea = current;
            break;
          }
          current = current.nextElementSibling;
        }

        // If not found, look in parent
        if (!this.targetTextarea && this.parentElement) {
          const textarea = this.parentElement.querySelector('textarea');
          if (textarea) {
            this.targetTextarea = textarea;
          }
        }
      }
    }

    // Attach keyboard shortcuts to textarea
    if (this.targetTextarea) {
      this.targetTextarea.addEventListener('keydown', this.boundHandleKeydown);
    }
  }

  render() {
    const showSparkle = this.getAttribute('show-sparkle') !== 'false';
    const helpOpen = this.helpPaneOpen || false;

    this.innerHTML = `
      <div class="markdown-toolbar">
        <button type="button" class="toolbar-btn toolbar-btn-bold" title="Bold (Ctrl+B)">
          <span class="material-symbols-outlined bold-icon">format_bold</span>
        </button>
        <button type="button" class="toolbar-btn toolbar-btn-italic" title="Italic (Ctrl+I)">
          <span class="material-symbols-outlined italic-icon">format_italic</span>
        </button>
        <div class="toolbar-divider"></div>
        <button type="button" class="toolbar-btn toolbar-btn-blockquote" title="Blockquote">
          <span class="material-symbols-outlined dance-icon">format_quote</span>
        </button>
        <button type="button" class="toolbar-btn toolbar-btn-code" title="Code">
          <span class="material-symbols-outlined dance-icon">code</span>
        </button>
        <div class="toolbar-divider"></div>
        <button type="button" class="toolbar-btn toolbar-btn-unordered-list" title="Unordered List">
          <span class="material-symbols-outlined list-icon">format_list_bulleted</span>
        </button>
        <button type="button" class="toolbar-btn toolbar-btn-ordered-list" title="Ordered List">
          <span class="material-symbols-outlined list-icon">format_list_numbered</span>
        </button>
        <div class="toolbar-divider"></div>
        <button type="button" class="toolbar-btn toolbar-btn-img" title="Image">
          <span class="material-symbols-outlined image-icon">image</span>
        </button>
        <button type="button" class="toolbar-btn toolbar-btn-link" title="Link">
          <span class="material-symbols-outlined link-icon">link</span>
        </button>
        ${showSparkle ? '<div class="toolbar-divider"></div>' : ''}
        ${showSparkle ? `
          <button type="button" class="toolbar-btn toolbar-btn-sparkle" title="Add a Sparkle">
            <span class="material-symbols-outlined sparkle-icon">attach_file</span>
          </button>
        ` : ''}
        <div class="toolbar-divider"></div>
        <button type="button" class="toolbar-btn toolbar-btn-help ${helpOpen ? 'active' : ''}" title="Help">
          <span class="material-symbols-outlined help-icon">help_outline</span>
        </button>
      </div>
      <div class="markdown-help-pane ${helpOpen ? 'open' : ''}">
        <div class="help-pane-content">
          <div class="close-help">Click the help button again to close the help pane. ↗️</div>
          <details class="help-section" open="true">
            <summary>📝 Formatting Help</summary>
            <div class="help-section-content">
              <p>Fun Old Times uses Markdown for formatting. Markdown is a simple, human-readable text format that converts to beautifully formatted posts.</p>
              <ul class="markdown-examples">
                <li>You can wrap text in <code>**bold**</code> or <code>*italic*</code> to make it <strong>bold</strong> or <em>italic</em>.</li>
                <li>You can also use <code>&gt;</code> to make text a blockquote.</li>
                <li>You can use <code>\`\`\`</code> to make text a code block.</li>
                <li>You can use <code>-</code> to make text a list item.</li>
                <li>You can use <code>1.</code> to make text a numbered list item.</li>
                <li>You can use <code>![caption](url)</code> to insert an image.</li>
                <li>You can use <code>[text](url)</code> to insert a link.</li>
              </ul>
              <p>You can also use the buttons defined below.</p>
            </div>
          </details>
          <details class="help-section" open="true">
            <summary>🎨 Toolbar Buttons</summary>
            <div class="help-section-content">
              <div class="help-button-grid">
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon bold-icon">format_bold</span>
                  <strong>Bold</strong> <kbd>Ctrl+B</kbd> - Makes text <strong>bold</strong> by wrapping it in double asterisks
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon italic-icon">format_italic</span>
                  <strong>Italic</strong> <kbd>Ctrl+I</kbd> - Makes text <em>italic</em> by wrapping it in single asterisks
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon dance-icon">format_quote</span>
                  <strong>Blockquote</strong> - Formats text as a quote by adding <code>&gt;</code> at the start of each line
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon dance-icon">code</span>
                  <strong>Code</strong> - Wraps text in backticks for inline code or triple backticks for code blocks
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon list-icon">format_list_bulleted</span>
                  <strong>Unordered List</strong> - Creates a bulleted list with dashes (<code>-</code>)
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon list-icon">format_list_numbered</span>
                  <strong>Ordered List</strong> - Creates a numbered list (<code>1.</code>, <code>2.</code>, <code>3.</code>)
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon image-icon">image</span>
                  <strong>Image</strong> - Inserts an image using <code>![caption](url)</code> format
                </div>
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon link-icon">link</span>
                  <strong>Link</strong> - Inserts a link using <code>[text](url)</code> format
                </div>
                ${showSparkle ? `
                <div class="help-button-item">
                  <span class="material-symbols-outlined help-button-icon sparkle-icon">attach_file</span>
                  <strong>Sparkle</strong> ✨ - Add photos and files from your connected cloud storage accounts (SmugMug, Google Drive)
                </div>
                ` : ''}
              </div>
            </div>
          </details>
          ${showSparkle ? `
          <details class="help-section" open="true">
            <summary>✨ Sparkle Sources</summary>
            <div class="help-section-content">
              <p>Sparkle is a feature that allows you to add photos and files from your connected cloud storage accounts directly into your posts. Fun
              Old Times only has access to the files you select to share with it. This prevents most ad trackers. 
              You can revoke access and all of your previously shared files will no longer be available in the app. 
              We value your privacy and security.</p>

              <p>To use the Sparkle button, you'll need to connect your cloud storage accounts first. This allows you to easily add photos and files from your SmugMug or Google Drive directly into your posts.</p>
              <a href="/settings/sparkle-sources" class="help-link-btn">⚙️ Go to Sparkle Sources Settings</a>
            </div>
          </details>
          ` : ''}
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    const boldBtn = this.querySelector('.toolbar-btn-bold');
    const italicBtn = this.querySelector('.toolbar-btn-italic');
    const blockquoteBtn = this.querySelector('.toolbar-btn-blockquote');
    const codeBtn = this.querySelector('.toolbar-btn-code');
    const unorderedListBtn = this.querySelector('.toolbar-btn-unordered-list');
    const orderedListBtn = this.querySelector('.toolbar-btn-ordered-list');
    const imgBtn = this.querySelector('.toolbar-btn-img');
    const linkBtn = this.querySelector('.toolbar-btn-link');
    const sparkleBtn = this.querySelector('.toolbar-btn-sparkle');
    const helpBtn = this.querySelector('.toolbar-btn-help');


    if (boldBtn) {
      boldBtn.addEventListener('click', () => {
        this.insertBold();
      });
    }

    if (italicBtn) {
      italicBtn.addEventListener('click', () => {
        this.insertItalic();
      });
    }

    if (blockquoteBtn) {
      blockquoteBtn.addEventListener('click', () => {
        this.insertBlockquote();
      });
    }

    if (codeBtn) {
      codeBtn.addEventListener('click', () => {
        this.insertCode();
      });
    }

    if (unorderedListBtn) {
      unorderedListBtn.addEventListener('click', () => {
        this.insertUnorderedList();
      });
    }

    if (orderedListBtn) {
      orderedListBtn.addEventListener('click', () => {
        this.insertOrderedList();
      });
    }

    if (imgBtn) {
      imgBtn.addEventListener('click', () => {
        this.insertImg();
      });
    }

    if (linkBtn) {
      linkBtn.addEventListener('click', () => {
        this.insertLink();
      });
    }

    if (sparkleBtn) {
      sparkleBtn.addEventListener('click', () => {
        this.handleSparkleClick();
      });
    }

    if (helpBtn) {
      helpBtn.addEventListener('click', () => {
        this.handleHelpClick();
      });
    }
  }

  handleKeydown(e) {
    // Ctrl/Cmd + B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      this.insertBold();
    }
    // Ctrl/Cmd + I for italic
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      this.insertItalic();
    }
  }

  insertBold() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    const textToInsert = `**${selectedText}**`;
    insertTextAtCursor(textarea, textToInsert, { ensureTrailingNewline: false });

    // If no text was selected, position cursor between the markers
    const newPositionIncrement = 2;
    if (selectedText) {
      // Position cursor after the inserted text
      newPositionIncrement + textToInsert.length;
    }
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertItalic() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let textToInsert;
    if (selectedText) {
      // If text is selected, wrap it in italic markers
      textToInsert = `*${selectedText}*`;
    } else {
      // If no text selected, insert italic markers with cursor in between
      textToInsert = '**';
    }

    insertTextAtCursor(textarea, textToInsert, { ensureTrailingNewline: false });

    // If no text was selected, position cursor between the markers
    if (!selectedText) {
      const newPosition = start + 1;
      textarea.setSelectionRange(newPosition, newPosition);
    } else {
      // Position cursor after the inserted text
      const newPosition = start + textToInsert.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }

    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertBlockquote() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const lines = selectedText.split('\n');
    const value = textarea.value;
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    // Get the line start position
    const lineStart = beforeText.lastIndexOf('\n') + 1;
    const currentLine = beforeText.substring(lineStart);

    let textToInsert;
    if (selectedText) {
      // If text is selected, wrap each line with >
      const quotedLines = lines.map(line => line ? `> ${line}` : '>').join('\n');
      textToInsert = quotedLines;
    } else {
      // If no text selected, insert blockquote marker
      if (currentLine.trim() === '') {
        // Empty line - insert > with space
        textToInsert = '> ';
      } else {
        // Non-empty line - insert > at start of line
        const beforeLine = beforeText.substring(0, lineStart);
        const newLineStart = beforeLine + '> ';
        textarea.value = newLineStart + currentLine + afterText;
        const newPosition = newLineStart.length + currentLine.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
    }

    // Replace selected text
    textarea.value = beforeText + textToInsert + afterText;
    const newPosition = start + textToInsert.length;
    textarea.setSelectionRange(newPosition, newPosition);
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertCode() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let textToInsert;
    if (selectedText) {
      // If text is selected, check if it contains newlines
      if (selectedText.includes('\n')) {
        // Multi-line code block
        textToInsert = `\`\`\`\n${selectedText}\n\`\`\``;
      } else {
        // Inline code
        textToInsert = `\`${selectedText}\``;
      }
    } else {
      // If no text selected, insert inline code markers
      textToInsert = '``';
    }

    insertTextAtCursor(textarea, textToInsert, { ensureTrailingNewline: false });

    // If no text was selected, position cursor between the markers
    if (!selectedText) {
      const newPosition = start + 1;
      textarea.setSelectionRange(newPosition, newPosition);
    } else {
      // Position cursor after the inserted text
      const newPosition = start + textToInsert.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }

    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertUnorderedList() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const value = textarea.value;
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    let textToInsert;
    if (selectedText) {
      // If text is selected, convert each line to a list item
      const lines = selectedText.split('\n');
      const listItems = lines
        .filter(line => line.trim())
        .map(line => `- ${line.trim()}`)
        .join('\n');
      textToInsert = listItems;
    } else {
      // If no text selected, insert a list item
      textToInsert = '- ';
    }

    // Replace selected text
    textarea.value = beforeText + textToInsert + afterText;
    const newPosition = start + textToInsert.length;
    textarea.setSelectionRange(newPosition, newPosition);
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertOrderedList() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const value = textarea.value;
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    let textToInsert;
    if (selectedText) {
      // If text is selected, convert each line to a numbered list item
      const lines = selectedText.split('\n');
      let itemNumber = 1;
      const listItems = lines
        .filter(line => line.trim())
        .map(line => {
          const item = `${itemNumber}. ${line.trim()}`;
          itemNumber++;
          return item;
        })
        .join('\n');
      textToInsert = listItems;
    } else {
      // If no text selected, insert a numbered list item
      textToInsert = '1. ';
    }

    // Replace selected text
    textarea.value = beforeText + textToInsert + afterText;
    const newPosition = start + textToInsert.length;
    textarea.setSelectionRange(newPosition, newPosition);
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertImg() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    let selectedText = textarea.value.substring(start, end);
    if (!selectedText) {
      selectedText = 'Enter Image Caption Here';
    }
    const imgUrl = prompt('Enter a URL for this image:');
    if (!imgUrl)
      return;


    const textToInsert = `![${selectedText}](${imgUrl})`;
    insertTextAtCursor(textarea, textToInsert, { ensureTrailingNewline: false });

    // Position cursor after the inserted text
    const newPosition = start + textToInsert.length;
    textarea.setSelectionRange(newPosition, newPosition);

    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertLink() {
    const textarea = this.getTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    let selectedText = textarea.value.substring(start, end);
    if (!selectedText) {
      selectedText = 'Enter Link Text Here';
    }
    const linkUrl = prompt('Enter a URL for this link:');
    if (!linkUrl)
      return;


    const textToInsert = `[${selectedText}](${linkUrl})`;
    insertTextAtCursor(textarea, textToInsert, { ensureTrailingNewline: false });

    // Position cursor after the inserted text
    const newPosition = start + textToInsert.length;
    textarea.setSelectionRange(newPosition, newPosition);

    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  handleSparkleClick() {
    // Dispatch event for parent component to handle
    this.dispatchEvent(new CustomEvent('toolbar-sparkle-click', {
      bubbles: true,
      detail: { textarea: this.getTextarea() }
    }));
  }

  handleHelpClick() {
    this.helpPaneOpen = !this.helpPaneOpen;
    this.render();
    this.attachEventListeners();
  }

  getTextarea() {
    if (!this.targetTextarea) {
      this.findTargetTextarea();
    }
    return this.targetTextarea;
  }

  setTextarea(textarea) {
    // Remove old listener
    if (this.targetTextarea) {
      this.targetTextarea.removeEventListener('keydown', this.boundHandleKeydown);
    }

    // Set new textarea
    this.targetTextarea = textarea;

    // Add new listener
    if (this.targetTextarea) {
      this.targetTextarea.addEventListener('keydown', this.boundHandleKeydown);
    }
  }
}

// Register the custom element
customElements.define('markdown-toolbar', MarkdownToolbar);

